<template>
  <div
    ref="backdropRef"
    :class="[$style.config, $style.wrap]"
    :style="{
      background: instanceOfBackdrop(backdrop)
        ? backdrop?.fields?.background
        : undefined,
      backgroundColor: instanceOfBackdrop(backdrop)
        ? backdrop?.fields?.backgroundColor
        : undefined,
    }"
  >
    <div
      v-if="computedBackgroundImage"
      :key="computedBackdropKey"
      :class="$style.backdropContainer"
    >
      <ResponsiveImage
        v-if="instanceOfResponsiveImage(computedBackgroundImage)"
        :class="$style.picture"
        :srcset="computedBackgroundImage"
        :quality="computedQuality"
        :query="computedQuery"
        :eager="eager || false"
        alt=""
      />
      <img
        v-else
        aria-hidden="true"
        :class="$style.img"
        :src="computedBackgroundImage"
        alt=""
      />
    </div>
    <slot :is-intersecting="isIntersecting" />
  </div>
</template>

<script setup lang="ts">
import { useIntersectionObserver } from '~/composables/observeIntersection'
import { instanceOfBackdrop } from '~/utils/instanceOfEntry'

import type {
  TypeBackdropWithoutUnresolvableLinksResponse,
  TypeResponsiveImageWithoutUnresolvableLinksResponse,
} from '~/types/contentful'
import type { Asset } from 'contentful'

const backdropRef = ref(null)

const { isIntersecting } = useIntersectionObserver(backdropRef, {}, true)

const props = withDefaults(
  defineProps<{
    as?: string
    backdrop?:
      | TypeBackdropWithoutUnresolvableLinksResponse
      | TypeResponsiveImageWithoutUnresolvableLinksResponse
      | Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>
      | string
      | null
    observeIntersection?: boolean
    isStatic?: boolean
    eager?: boolean
    backdropMinHeight?: number | null
    fit?: 'pad' | 'fill' | 'scale' | 'crop' | 'thumb'
    f?:
      | 'center'
      | 'top'
      | 'right'
      | 'left'
      | 'bottom'
      | 'top_right'
      | 'top_left'
      | 'bottom_right'
      | 'bottom_left'
      | 'face'
      | 'faces'
    quality?: number
  }>(),
  {
    as: 'div',
    backdrop: null,
    observeIntersection: true,
    isStatic: false,
    eager: false,
    backdropMinHeight: null,
    fit: 'crop',
    f: 'center',
    quality: 80,
  },
)

const computedBackgroundImage = computed(() => {
  if (instanceOfBackdrop(props.backdrop))
    return props.backdrop?.fields?.backgroundImage
  if (instanceOfAsset(props.backdrop)) return props.backdrop?.fields?.file?.url
  return props.backdrop || ''
})

const computedBackdropKey = computed(() => {
  if (instanceOfBackdrop(props.backdrop)) return props.backdrop?.sys?.id
  if (instanceOfAsset(props.backdrop)) return props.backdrop?.sys?.id
  if (instanceOfResponsiveImage(props.backdrop)) return props.backdrop?.sys?.id
  return props.backdrop || ''
})

const computedQuery = computed(() => {
  const queryProps: Record<string, string | number | undefined | null> = {
    fit: props.fit,
    f: props.f,
    h: props.backdropMinHeight,
  }
  return (
    '&' +
    Object.keys(queryProps)
      .filter((key) => queryProps[key])
      .map((key) => `${key}=${queryProps[key]}`)
      .join('&')
  )
})

const computedQuality = computed(() =>
  !props.observeIntersection || isIntersecting ? props.quality : 1,
)
</script>

<style lang="scss" module>
.config {
  --static-width: 100%;

  @include media('xs') {
    --static-width: 767px;
  }

  @include media('sm') {
    --static-width: 1023px;
  }

  @include media('md') {
    --static-width: 1920px;
  }
}

.wrap {
  position: relative;
  overflow: var(--backdrop-overflow, hidden);
  z-index: 0;

  .picture {
    display: block;
    height: 100%;
  }
}

.backdropContainer {
  pointer-events: none;
  position: absolute;
  top: -1px;
  right: -1px;
  bottom: -1px;
  left: -1px;
  overflow: hidden;
  z-index: -1;
}

.backdrop {
  position: absolute;
  top: 0;
  left: 50%;
  width: 100%;
  transform: translateX(-50%);
  bottom: 0;

  z-index: -1;
  overflow: hidden;

  &.isStatic {
    bottom: unset;
    height: auto;
    width: var(--static-width);
    max-height: 100%;
  }
}

.img {
  height: 100%;
  object-fit: cover;
  object-position: right center;
  width: 100%;
}
</style>
